 import { useAuth } from "@/contexts/AuthContext";
 import { Navigate, useLocation } from "react-router-dom";
 import { ReactNode, useState, useEffect } from "react";
 import { supabase } from "@/integrations/supabase/client";
 import { Button } from "@/components/ui/button";
 import { Input } from "@/components/ui/input";
 import { Label } from "@/components/ui/label";
 import { toast } from "sonner";
 import { ShieldCheck, Lock } from "lucide-react";

export const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center">Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

 export const AdminRoute = ({ children }: { children: ReactNode }) => {
   const { user, isAdmin, isSuperAdmin, loading } = useAuth();
   if (loading) return <div className="flex min-h-screen items-center justify-center">Cargando...</div>;
   if (!user) return <Navigate to="/login" replace />;
   if (!isAdmin && !isSuperAdmin) return <Navigate to="/" replace />;
   return <>{children}</>;
 };
 
  export const ModeratorRoute = ({ children }: { children: ReactNode }) => {
    const { user, isAdmin, isModerator, isSuperAdmin, loading } = useAuth();
    if (loading) return <div className="flex min-h-screen items-center justify-center">Cargando...</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (!isAdmin && !isModerator && !isSuperAdmin) return <Navigate to="/" replace />;
    return <>{children}</>;
  };
 
  export const SuperAdminRoute = ({ children }: { children: ReactNode }) => {
    const { user, loading, isSuperAdmin: contextIsSuperAdmin } = useAuth();
    const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);
   const [checkLoading, setCheckLoading] = useState(true);
   const [mfaVerified, setMfaVerified] = useState<boolean>(false);
   const [mfaEnabled, setMfaEnabled] = useState<boolean>(false);
   const [mfaCode, setMfaCode] = useState("");
   const [verifying, setVerifying] = useState(false);
   const location = useLocation();
 
    useEffect(() => {
      const check = async () => {
        if (!user) {
          setCheckLoading(false);
          return;
        }
        
        // Use context value if available, otherwise check RPC
        const isSuper = contextIsSuperAdmin;
        setIsSuperAdmin(isSuper);
        
        if (isSuper) {
         // Check if MFA is enrolled for this user
         const { data: factors, error } = await supabase.auth.mfa.listFactors();
         const hasMfa = factors?.all?.some(f => f.status === 'verified') || false;
         setMfaEnabled(hasMfa);
         
         // Check if already verified in this session
         const { data: { session } } = await supabase.auth.getSession();
         const isVerified = session?.user?.factors?.some(f => f.status === 'verified') || !hasMfa;
         setMfaVerified(isVerified);
       }
       
       setCheckLoading(false);
     };
     check();
   }, [user]);
 
   const handleVerifyMfa = async () => {
     setVerifying(true);
     try {
       const { data: factors } = await supabase.auth.mfa.listFactors();
       const factor = factors?.all?.find(f => f.status === 'verified');
       
       if (!factor) throw new Error("No hay factor MFA configurado");
 
       const { data, error } = await supabase.auth.mfa.challenge({ factorId: factor.id });
       if (error) throw error;
 
       const { error: verifyError } = await supabase.auth.mfa.verify({
         factorId: factor.id,
         challengeId: data.id,
         code: mfaCode
       });
 
       if (verifyError) throw verifyError;
       setMfaVerified(true);
       toast.success("Identidad confirmada");
     } catch (err: any) {
       toast.error(err.message || "Error al verificar código");
     } finally {
       setVerifying(false);
     }
   };
 
   if (loading || checkLoading) return <div className="flex min-h-screen items-center justify-center">Cargando seguridad...</div>;
   if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
   if (isSuperAdmin === false) return <Navigate to="/403" replace />;
 
   if (isSuperAdmin && mfaEnabled && !mfaVerified) {
     return (
       <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
         <div className="w-full max-w-md space-y-8 rounded-2xl border bg-card p-8 shadow-xl">
           <div className="text-center">
             <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
               <Lock className="h-8 w-8" />
             </div>
             <h2 className="text-2xl font-bold tracking-tight">Verificación Requerida</h2>
             <p className="mt-2 text-sm text-muted-foreground">
               Como Superadmin, debes confirmar tu identidad mediante el segundo factor (MFA).
             </p>
           </div>
           <div className="space-y-4">
             <div className="space-y-2">
               <Label htmlFor="mfa-code">Código de autenticación</Label>
               <Input
                 id="mfa-code"
                 placeholder="000000"
                 value={mfaCode}
                 onChange={(e) => setMfaCode(e.target.value)}
                 className="text-center text-2xl tracking-[0.5em]"
                 maxLength={6}
               />
             </div>
             <Button 
               className="w-full" 
               onClick={handleVerifyMfa} 
               disabled={verifying || mfaCode.length < 6}
             >
               {verifying ? "Verificando..." : "Confirmar Acceso"}
             </Button>
           </div>
         </div>
       </div>
     );
   }
 
   return <>{children}</>;
 };
